import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import type {
  AdmissionApplication, AdmissionCycle, IdNamePair,
} from '../../../types/admissions';
import {
  Page, Section, Pill, applicationStatusTone, DataTable, Signatures, StatStrip,
  type DataTableColumn,
  pdfColors, pdfFontSize,
  fmtDate, fmtNumber, fmtMarks, fmtClassName, humanize,
  type SchoolBranding, type DocumentMeta,
} from '../../../utils/pdf';
import { FormSections, printableSections } from './sharedFieldRendering';
import { applicationFieldValue, type ApplicationValueSources } from '../form/applicationFieldValues';
import { pathwayFor, roundMax, roundNoun } from '../../../utils/admissionAssessment';

interface Props extends Omit<ApplicationValueSources, 'app'> {
  app: AdmissionApplication;
  cycle: AdmissionCycle;
  branding: SchoolBranding;
  meta: DocumentMeta;
  /** The applicant's photo as a data URL; without one the form shows the affix box. */
  photoSrc?: string | null;
}

const styles = StyleSheet.create({
  statusRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  statusLabel: { fontSize: pdfFontSize.xs, color: pdfColors.textMuted, marginRight: 8 },

  // ── Timeline ──────────────────────────────────────────────────────────
  tlRow: { flexDirection: 'row' },
  tlRail: { width: 16, alignItems: 'center' },
  tlDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    marginTop: 3,
    backgroundColor: pdfColors.surface,
    borderWidth: 1.5,
    borderColor: pdfColors.brand,
    borderStyle: 'solid',
  },
  tlDotLatest: { backgroundColor: pdfColors.brand },
  tlLine: { flex: 1, width: 1, backgroundColor: pdfColors.dividerStrong, marginTop: 3 },
  tlBody: { flex: 1, paddingLeft: 6, paddingBottom: 10 },
  tlTitle: { fontSize: pdfFontSize.sm, fontWeight: 600, color: pdfColors.textPrimary },
  tlMeta: { fontSize: pdfFontSize.xs, color: pdfColors.textMuted, marginTop: 1.5 },
});

function refName(ref: string | IdNamePair | null | undefined): string | null {
  return ref && typeof ref === 'object' && ref.name ? ref.name : null;
}

const OBJECT_ID = /\b[a-f0-9]{24}\b/gi;

/** Timeline reasons sometimes embed raw ObjectIds ("invoice 6ac6… paid").
 *  They mean nothing on paper — drop them and tidy what remains. */
function readableReason(reason?: string): string {
  if (!reason) return '';
  const cleaned = reason.replace(OBJECT_ID, '').replace(/\s{2,}/g, ' ').trim();
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : '';
}

interface ScoreRow { item: string; detail: string; score: string }

/** FilledApplicationDocument — the application as the parent filled it:
 *  the same form, field for field, as the blank form, with every answer
 *  written in — followed by the office's assessment, documents and history. */
const FilledApplicationDocument: React.FC<Props> = ({ app, cycle, branding, meta, guardians, photoSrc }) => {
  const pathway = pathwayFor(cycle, app.classId);
  const sources: ApplicationValueSources = { app, guardians };

  const scoreRows: ScoreRow[] = [
    ...(app.testScores || []).map((s) => ({
      item: s.subject,
      detail: 'Entry test',
      score: s.absent ? 'Absent' : `${fmtMarks(s.obtained)} / ${fmtMarks(s.maxMarks)}`,
    })),
    ...(app.interviewRounds || []).map((r) => {
      const round = pathway.interviewConfig?.rounds?.find((c) => c.key === r.roundKey);
      // Unscored answers ride along: "Interview attended by: Both parents".
      const answers = (r.rubricScores || []).filter((x) => x.kind && x.kind !== 'marks' && x.answer).map((x) => `${x.criterion}: ${x.answer}`);
      return {
        item: round?.name || humanize(r.roundKey),
        detail: [round ? roundNoun(round) : 'Interview', r.recommendation && humanize(r.recommendation), ...answers].filter(Boolean).join(' · '),
        score: round ? `${fmtMarks(r.totalMarks)} / ${fmtMarks(roundMax(round))}` : fmtMarks(r.totalMarks),
      };
    }),
  ];
  const scoreCols: Array<DataTableColumn<ScoreRow>> = [
    { key: 'item', header: 'Assessment', flex: 2, render: (r) => r.item },
    { key: 'detail', header: 'Type', flex: 2, render: (r) => r.detail },
    { key: 'score', header: 'Score', width: 90, align: 'right', render: (r) => r.score },
  ];

  const docCols: Array<DataTableColumn<AdmissionApplication['documents'][number]>> = [
    { key: 'doc', header: 'Document', flex: 2,
      render: (d) => pathway.documentChecklist?.find((c) => c.key === d.key)?.label || humanize(d.key) },
    { key: 'file', header: 'File', flex: 2.5, render: (d) => d.fileName || d.filename || '—' },
    { key: 'status', header: 'Status', width: 80,
      render: (d) => (
        <Pill
          dense
          label={humanize(d.status)}
          tone={d.status === 'verified' ? 'success' : d.status === 'rejected' ? 'danger' : d.status === 'uploaded' ? 'info' : 'neutral'}
        />
      ) },
  ];

  const className = refName(app.classId);
  // Who the application is for, under the letterhead.
  const details = [
    { value: cycle.name },
    { label: 'Applicant', value: app.name },
    ...(className ? [{ value: fmtClassName(className) }] : []),
  ];

  const timeline = (app.timeline || []).slice().reverse().slice(0, 20);
  let n = printableSections(cycle).length;

  return (
    <Document title={meta.title} author={branding.name}>
      <Page branding={branding} meta={{ ...meta, signed: true }} official={{ details }}>
        {/* ── Record strip ──────────────────────────────── */}
        <View style={styles.statusRow} wrap={false}>
          <Text style={styles.statusLabel}>Application status</Text>
          <Pill label={humanize(app.status)} tone={applicationStatusTone(app.status)} />
        </View>
        <StatStrip
          dense
          items={[
            { label: 'Application no.', value: app.applicationNumber, weight: 1.7 },
            { label: 'Roll number', value: app.admitCard?.rollNumber || '—', weight: app.admitCard?.rollNumber ? 1.7 : 1 },
            { label: 'Applied on', value: fmtDate(app.createdAt) },
            {
              label: 'Merit',
              value: app.computedMeritScore != null ? fmtNumber(app.computedMeritScore, 2) : '—',
              caption: app.meritRank != null ? `Rank ${app.meritRank}` : undefined,
            },
          ]}
        />

        {/* ── The form, filled in ───────────────────────── */}
        <FormSections cycle={cycle} answerFor={(key) => applicationFieldValue(key, sources)} photoSrc={photoSrc} />

        {/* ── Assessment ────────────────────────────────── */}
        {scoreRows.length > 0 && (
          <Section index={++n} title="Assessment">
            <DataTable columns={scoreCols} rows={scoreRows} compact />
          </Section>
        )}

        {/* ── Documents ─────────────────────────────────── */}
        {app.documents?.length > 0 && (
          <Section index={++n} title="Documents" caption={`${app.documents.length} on file`}>
            <DataTable columns={docCols} rows={app.documents} compact />
          </Section>
        )}

        {/* ── Timeline ──────────────────────────────────── */}
        {timeline.length > 0 && (
          <Section
            index={++n}
            title="Application history"
            caption={app.timeline.length > timeline.length ? `Latest ${timeline.length} of ${app.timeline.length}` : undefined}
          >
            {timeline.map((t, i) => {
              const title = t.to
                ? (t.from ? `${humanize(t.from)} → ${humanize(t.to)}` : humanize(t.to))
                : (t.label || humanize(t.event || 'Update'));
              const detail = [fmtDate(t.at), refName(t.by), readableReason(t.reason)].filter(Boolean).join('  ·  ');
              return (
                <View key={i} style={styles.tlRow} wrap={false}>
                  <View style={styles.tlRail}>
                    <View style={i === 0 ? [styles.tlDot, styles.tlDotLatest] : styles.tlDot} />
                    {i < timeline.length - 1 && <View style={styles.tlLine} />}
                  </View>
                  <View style={styles.tlBody}>
                    <Text style={styles.tlTitle}>{title}</Text>
                    <Text style={styles.tlMeta}>{detail}</Text>
                  </View>
                </View>
              );
            })}
          </Section>
        )}

        {/* ── Office endorsement ────────────────────────── */}
        <Section index={++n} title="Office endorsement" keepTogether>
          <Signatures slots={['Verified by', 'Admissions officer', 'Date']} />
        </Section>
      </Page>
    </Document>
  );
};

export default FilledApplicationDocument;
